import React, { forwardRef, Ref, useId, useImperativeHandle, useRef } from 'react';
import styled, { css } from 'styled-components';
import ReactTooltip, { TooltipProps as ReactTooltipProps } from 'react-tooltip';

import { useTheme, Theme } from '@/services/theme';

import { SpriteIcon } from '../SpriteIcon';

export type TooltipProps = {
  iconClassName?: string;
  bodyClassName?: string;
  iconElement?: React.ReactNode;
  icon?: string;
  innerContent: React.ReactNode;
  innerContentStyle?: React.CSSProperties;
  clickable?: boolean;
  dataEvent?: string;
} & React.HTMLProps<HTMLSpanElement> &
  Partial<ReactTooltipProps>;

const StyledTooltip = styled.span`
  display: inline-flex;
  position: relative;
  max-width: 300px;
`;

const StyledIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 1.6rem;
  width: 1.6rem;

  background-color: ${({ theme }: { theme: Theme }) => theme.colors.gray25};
  border-radius: 50%;
  cursor: pointer;
`;

const StyledTooltipContainer = styled.div`
  z-index: 10;
  position: relative;
`;

const TooltipWrapper = styled(ReactTooltip)`
  border-radius: 8px !important;
  opacity: 1 !important;
  background: ${({ theme }: { theme: Theme }) => theme.colors.newGray5} !important;

  &:after {
    opacity: 1 !important;
    background: ${({ theme }: { theme: Theme }) => theme.colors.newGray5} !important;
  }

  ${(props: { clickable?: boolean }) =>
    props.clickable &&
    css`
      pointer-events: auto !important;

      &:hover {
        visibility: visible !important;
        opacity: 1 !important;
      }
    `}
`;

export type TooltipRef = {
  close: () => void;
};

const CustomTooltip = forwardRef<TooltipRef, TooltipProps>(
  (
    {
      innerContent,
      innerContentStyle,
      iconClassName,
      bodyClassName,
      iconElement,
      icon = 'i',
      delayHide,
      effect,
      place = 'bottom',
      clickable,
      dataEvent,
      globalEventOff,
      ...props
    },
    forwardedRef,
  ) => {
    const { theme } = useTheme();
    const tooltipRef = useRef<ReactTooltip & { tooltipRef: Ref<HTMLElement> }>(null);

    const id = useId();

    useImperativeHandle(
      forwardedRef,
      () => ({
        close: () => {
          // See: https://github.com/ReactTooltip/react-tooltip/issues/449
          if (tooltipRef.current && tooltipRef.current.tooltipRef) {
            tooltipRef.current.tooltipRef = null;
            ReactTooltip.hide();
          }
        },
      }),
      [],
    );

    return (
      <>
        <StyledTooltip
          ref={tooltipRef}
          data-tip
          data-for={id}
          data-event={dataEvent}
          className={iconClassName}
          theme={theme}
          {...props}
        >
          {iconElement !== undefined ? (
            iconElement
          ) : (
            <StyledIconWrap theme={theme}>
              <SpriteIcon icon={icon} color={theme.colors.text.primary} size={0.8} />
            </StyledIconWrap>
          )}
        </StyledTooltip>

        <TooltipWrapper
          ref={tooltipRef}
          id={id}
          theme={theme}
          className={`GLOBAL_TOOLTIP ${bodyClassName}`}
          place={place}
          backgroundColor={theme.colors.background.tooltip}
          delayHide={delayHide}
          effect={effect}
          clickable={clickable}
          globalEventOff={globalEventOff}
        >
          <StyledTooltipContainer style={innerContentStyle}>{innerContent}</StyledTooltipContainer>
        </TooltipWrapper>
      </>
    );
  },
);

export { CustomTooltip as Tooltip };
